// Real iTunes Search API Service
// Calls the actual iTunes API for song search with autocomplete functionality

export interface SongSearchResult {
  trackId: number;
  trackName: string;
  artistName: string;
  collectionName: string; // Album name
  artworkUrl60: string;
  artworkUrl100: string;
  trackTimeMillis: number;
  trackExplicitness: 'explicit' | 'notExplicit' | 'cleaned';
  previewUrl?: string;
  isrc?: string | null;
}

interface iTunesApiResponse {
  resultCount: number;
  results: Array<{
    trackId: number;
    trackName: string;
    artistName: string;
    collectionName: string;
    artworkUrl60: string;
    artworkUrl100: string;
    trackTimeMillis: number;
    trackExplicitness: string;
    previewUrl?: string;
    kind?: string;
  }>;
}

/**
 * Search for songs using the real iTunes Search API
 * @param query Search query (song title or artist name)
 * @param limit Maximum number of results (default: 10)
 * @returns Promise resolving to array of search results
 */
export async function searchItunes(query: string, limit: number = 10, mode: 'song' | 'artist' = 'song'): Promise<SongSearchResult[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }

  try {
    // Call the iTunes Search API
    // Artist mode: use higher limit to show more songs by the artist
    const effectiveLimit = mode === 'artist' ? Math.max(limit, 25) : limit;
    const encodedQuery = encodeURIComponent(query);
    const attributeParam = mode === 'artist' ? '&attribute=artistTerm' : '';
    const url = `https://itunes.apple.com/search?term=${encodedQuery}&entity=song&limit=${effectiveLimit}${attributeParam}`;

    const response = await fetch(url);

    if (!response.ok) {
      console.error('iTunes API error:', response.status, response.statusText);
      // Fall back to empty results on API error
      return [];
    }

    const data: iTunesApiResponse = await response.json();

    // Filter to only include songs (not music videos, etc.)
    const songs: SongSearchResult[] = data.results
      .filter(item => item.kind === 'song' || !item.kind) // Some results don't have 'kind' field
      .map(item => ({
        trackId: item.trackId,
        trackName: item.trackName,
        artistName: item.artistName,
        collectionName: item.collectionName || 'Unknown Album',
        artworkUrl60: item.artworkUrl60 || '',
        artworkUrl100: item.artworkUrl100 || '',
        trackTimeMillis: item.trackTimeMillis || 0,
        trackExplicitness: normalizeExplicitness(item.trackExplicitness),
        previewUrl: item.previewUrl,
        isrc: null
      }));

    return songs;
  } catch (error) {
    console.error('iTunes API fetch error:', error);
    // Return empty results on network error
    return [];
  }
}

/**
 * Normalize the explicitness field from iTunes API
 */
function normalizeExplicitness(value: string): 'explicit' | 'notExplicit' | 'cleaned' {
  if (value === 'explicit') return 'explicit';
  if (value === 'cleaned') return 'cleaned';
  return 'notExplicit';
}

/**
 * Get a song by its track ID from iTunes API
 * @param trackId iTunes track ID
 * @returns Song details or null if not found
 */
export async function lookupItunesTrack(trackId: number | string): Promise<SongSearchResult | null> {
  try {
    const url = `https://itunes.apple.com/lookup?id=${trackId}`;
    const response = await fetch(url);

    if (!response.ok) {
      return null;
    }

    const data: iTunesApiResponse = await response.json();

    if (data.resultCount === 0 || !data.results[0]) {
      return null;
    }

    const item = data.results[0];
    return {
      trackId: item.trackId,
      trackName: item.trackName,
      artistName: item.artistName,
      collectionName: item.collectionName || 'Unknown Album',
      artworkUrl60: item.artworkUrl60 || '',
      artworkUrl100: item.artworkUrl100 || '',
      trackTimeMillis: item.trackTimeMillis || 0,
      trackExplicitness: normalizeExplicitness(item.trackExplicitness),
      previewUrl: item.previewUrl,
      isrc: null
    };
  } catch (error) {
    console.error('iTunes API lookup error:', error);
    return null;
  }
}

/**
 * Format track duration from milliseconds to mm:ss
 */
export function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Check if a song is explicit or a cleaned version of an explicit song.
 * iTunes returns 'cleaned' for censored versions — these are safe to play.
 */
export function isExplicit(song: SongSearchResult): boolean {
  return song.trackExplicitness === 'explicit';
}
